import { getEndpoints, getConfig, getSupabaseClient } from './config.js';

/**
 * Lightswitch SDK Core Authentication
 * 
 * Handles JWT token management, authentication state, and user data.
 * Pure JavaScript implementation with no framework dependencies.
 */


// Authentication state
let authState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  authToken: null
};

// Event listeners for auth state changes
const authListeners = new Set();

/**
 * Get Supabase client instance with error handling
 * @returns {Object|null} Supabase client or null if not available
 */
const getSupabase = () => {
  const client = getSupabaseClient();
  if (!client) {
    console.warn('Supabase client not provided to Lightswitch SDK. Some auth features may not work.');
    return null;
  }
  return client;
};

/**
 * Helper function to parse JWT token
 * @param {string} token - JWT token to parse
 * @returns {Object|null} Decoded token payload or null if invalid
 */
const parseJwt = token => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Notify all listeners of auth state changes
 */
const notifyAuthListeners = () => {
  authListeners.forEach(listener => {
    try {
      listener({
        ...authState
      });
    } catch (error) {
      console.error('Error in auth listener:', error);
    }
  });
};

/**
 * Update authentication state
 * @param {Object} updates - Partial state updates
 */
const updateAuthState = updates => {
  authState = {
    ...authState,
    ...updates
  };
  notifyAuthListeners();
};

/**
 * Add listener for authentication state changes
 * @param {Function} listener - Callback function that receives auth state
 * @returns {Function} Unsubscribe function
 */
const onAuthChange = listener => {
  authListeners.add(listener);

  // Immediately call with current state
  listener({
    ...authState
  });

  // Return unsubscribe function
  return () => {
    authListeners.delete(listener);
  };
};

/**
 * Get current authentication state
 * @returns {Object} Current auth state
 */
const getAuthState = () => ({
  ...authState
});

/**
 * Check if user is currently authenticated
 * @returns {boolean} Authentication status
 */
const isAuthenticated = () => authState.isAuthenticated;

/**
 * Get current user data
 * @returns {Object|null} User data or null if not authenticated
 */
const getUser = () => authState.user;

/**
 * Get current auth token
 * @returns {string|null} Auth token or null if not authenticated
 */
const getAuthToken = () => authState.authToken;

/**
 * Store token in localStorage
 * @param {string} token - Token to store
 */
const storeToken = token => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('lightswitchAuthToken', token);
    }
  } catch (err) {
    console.error('Error saving to localStorage:', err);
  }
};

/**
 * Remove token from localStorage
 */
const removeStoredToken = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('lightswitchAuthToken');
    }
  } catch (err) {
    console.error('Error removing from localStorage:', err);
  }
};

/**
 * Get stored token from localStorage
 * @returns {string|null} Stored token or null
 */
const getStoredToken = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('lightswitchAuthToken');
    }
  } catch (err) {
    console.error('Error accessing localStorage:', err);
  }
  return null;
};

/**
 * Authenticate user with JWT token
 * @param {string} token - JWT token
 * @returns {Promise<Object>} Promise resolving to user data
 */
const authenticateWithToken = async token => {
  console.log('Authenticating with token:', token);
  try {
    updateAuthState({
      isLoading: true,
      error: null
    });

    // Store the token
    storeToken(token);

    // Parse the JWT token to get user info
    const decodedToken = parseJwt(token);
    if (!decodedToken) {
      throw new Error('Invalid token format');
    }

    // Check if token is expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (decodedToken.exp && decodedToken.exp < currentTime) {
      throw new Error('Token expired');
    }

    // Extract user info from decoded token
    const userData = {
      id: decodedToken.sub || decodedToken.id || '1',
      email: decodedToken.email || 'user@example.com',
      name: decodedToken.name || 'User'
    };
    updateAuthState({
      authToken: token,
      user: userData,
      isAuthenticated: true,
      isLoading: false,
      error: null
    });
    return userData;
  } catch (error) {
    console.error('Authentication error:', error);
    const errorMessage = error.message || 'Failed to authenticate with token';
    updateAuthState({
      authToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: errorMessage
    });

    // Clear stored token on error
    removeStoredToken();
    throw new Error(errorMessage);
  }
};

/**
 * Log out the current user from both Lightswitch and Supabase
 * @returns {Promise<void>}
 */
const logout = async () => {
  try {
    // Sign out of Supabase session if client is available
    const supabase = getSupabase();
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Failed to sign out of Supabase:', error);
        // Continue with Lightswitch logout even if Supabase fails
      }
    }

    // Clear Lightswitch auth state
    updateAuthState({
      authToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
    
    console.log('Successfully logged out');
  } catch (error) {
    console.error('Error during logout:', error);
    // Still clear the auth state even if there's an error
    updateAuthState({
      authToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: error.message
    });
  }
};

/**
 * Initialize authentication by checking for existing Supabase session
 * @returns {Promise<void>}
 */
const initAuth = async () => {
  try {
    // Check if there's an existing Supabase session
    const supabase = getSupabase();
    if (!supabase) {
      console.log('No Supabase client available, skipping session check');
      return;
    }

    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Failed to get Supabase session:', error);
      return;
    }

    if (session && session.user) {
      // Update Lightswitch auth state to match existing Supabase session
      const userData = {
        id: session.user.id,
        email: session.user.email || 'user@example.com',
        name: session.user.user_metadata?.full_name || session.user.email || 'User'
      };

      updateAuthState({
        authToken: session.access_token,
        user: userData,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      console.log('Initialized with existing Supabase session:', userData);
    } else {
      console.log('No existing Supabase session found');
    }
  } catch (error) {
    console.error('Failed to initialize auth:', error);
    updateAuthState({
      isLoading: false,
      error: error.message
    });
  }
};

/**
 * Redirect to login page
 * @param {string} [redirectUrl] - URL to redirect back to after login
 */
const login = redirectUrl => {
  if (typeof window === 'undefined') {
    console.error('Login redirect only available in browser environment');
    return;
  }
  getConfig();
  const endpoints = getEndpoints();

  // Use provided redirect URL or current page
  const redirect = redirectUrl || window.location.origin + window.location.pathname;
  const encodedRedirect = encodeURIComponent(redirect);

  // Redirect to Lightswitch login page
  window.location.href = `${endpoints.login}?redirect_uri=${encodedRedirect}`;
};

/**
 * Redirect to signup page
 * @param {string} [redirectUrl] - URL to redirect back to after signup
 */
const signup = redirectUrl => {
  if (typeof window === 'undefined') {
    console.error('Signup redirect only available in browser environment');
    return;
  }
  const endpoints = getEndpoints();

  // Use provided redirect URL or current page
  const redirect = redirectUrl || window.location.origin + window.location.pathname;
  const encodedRedirect = encodeURIComponent(redirect);

  // Redirect to Lightswitch signup page
  window.location.href = `${endpoints.signup}?redirect_uri=${encodedRedirect}`;
};

/**
 * Check URL for Supabase auth parameters and create session if found
 * Does one thing only: sets Supabase session from URL parameters
 * @returns {Promise<boolean>} True if tokens were found and session was created
 */
const checkUrlForToken = async () => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check for required Supabase auth parameters in URL
  const urlParams = new URLSearchParams(window.location.search);
  const accessToken = urlParams.get('access_token');
  const refreshToken = urlParams.get('refresh_token');
  const expiresAt = urlParams.get('expires_at');

  // If we have all required parameters, create Supabase session
  if (accessToken && refreshToken && expiresAt) {
    console.log('Found Supabase auth tokens in URL, creating session');
    
    try {
      // Clean the URL to remove sensitive tokens
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // Create Supabase session if client is available
      const supabase = getSupabase();
      if (!supabase) {
        console.warn('Supabase client not available, cannot create session from URL tokens');
        return false;
      }

      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_at: parseInt(expiresAt)
      });

      if (error) {
        console.error('Failed to set Supabase session:', error);
        return false;
      }

      console.log('Successfully created Supabase session');
      return true;
      
    } catch (error) {
      console.error('Error creating Supabase session:', error);
      return false;
    }
  }

  // No required parameters found - do nothing
  return false;
};

/**
 * Set Supabase session from a token (for external auth flows)
 * This is useful when users authenticate through external flows (like funnels)
 * and return with a Supabase token that needs to be set as the active session
 * 
 * @param {string} accessToken - The Supabase access token
 * @param {string} [refreshToken] - The Supabase refresh token (optional)
 * @param {number|string} [expiresAt] - Token expiration timestamp (optional)
 * @returns {Promise<boolean>} True if session was set successfully
 */
const setSupabaseSession = async (accessToken, refreshToken = null, expiresAt = null) => {
  console.log('🔄 Setting Supabase session from provided token', {
    tokenLength: accessToken?.length,
    hasRefreshToken: !!refreshToken,
    hasExpiresAt: !!expiresAt
  });
  
  if (!accessToken) {
    console.error('❌ Access token is required to set Supabase session');
    return false;
  }

  const supabase = getSupabase();
  if (!supabase) {
    console.warn('⚠️ Supabase client not available, cannot set session');
    return false;
  }

  try {
    const sessionData = {
      access_token: accessToken,
      refresh_token: refreshToken
    };

    // Add expires_at if provided
    if (expiresAt) {
      sessionData.expires_at = typeof expiresAt === 'string' ? parseInt(expiresAt) : expiresAt;
    }

    console.log('📤 Calling supabase.auth.setSession with data:', {
      hasAccessToken: !!sessionData.access_token,
      hasRefreshToken: !!sessionData.refresh_token,
      expiresAt: sessionData.expires_at
    });

    const { data, error } = await supabase.auth.setSession(sessionData);

    if (error) {
      console.error('❌ Failed to set Supabase session:', error);
      return false;
    }

    console.log('📥 Supabase setSession response:', {
      hasData: !!data,
      hasSession: !!data?.session,
      hasUser: !!data?.user,
      userEmail: data?.user?.email,
      sessionId: data?.session?.user?.id
    });

    if (data.session && data.user) {
      console.log('✅ Successfully set Supabase session for user:', data.user.email);
      
      // The Supabase auth state change listener should handle updating the Lightswitch auth state
      // But let's also force a refresh to ensure immediate state update
      setTimeout(async () => {
        try {
          console.log('Force refreshing auth state after setting Supabase session');
          await initAuth(); // Re-initialize auth to sync with new session
          console.log('✅ Auth state force-refreshed after session set');
        } catch (error) {
          console.error('Error force-refreshing auth state:', error);
        }
      }, 100);
      
      return true;
    } else {
      console.error('No session data returned from Supabase');
      return false;
    }
  } catch (error) {
    console.error('Error setting Supabase session:', error);
    return false;
  }
};

export { authenticateWithToken, checkUrlForToken, getAuthState, getAuthToken, getStoredToken, getUser, initAuth, isAuthenticated, login, logout, onAuthChange, parseJwt, removeStoredToken, setSupabaseSession, signup, storeToken };
//# sourceMappingURL=auth.js.map
